import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';

/**
 * /auth/callback — landing page after the API server completes an OAuth
 * exchange and 302s here with ?token=...
 *
 * The hc_token cookie is already set by the API server callback handler
 * (cookies on localhost are shared across ports), so all we need to do is
 * re-fetch /api/auth/me and bounce to the home route.
 */
export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, error, refresh } = useAuth();

  // Force one explicit refresh in case the cookie raced with mount.
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!loading && user) navigate('/', { replace: true });
  }, [loading, user, navigate]);

  const errParam = params.get('error');

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center px-6">
      <div className="text-center space-y-3 max-w-md">
        {errParam ? (
          <>
            <h1 className="text-xl text-gray-100">Sign-in failed</h1>
            <p className="text-sm text-gray-400">
              The OAuth provider returned an error ({errParam}). Try again from the
              login screen.
            </p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="mt-4 px-4 py-2 rounded bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm"
            >
              Back to sign in
            </button>
          </>
        ) : error ? (
          <>
            <h1 className="text-xl text-gray-100">Couldn't finish signing in</h1>
            <p className="text-sm text-gray-400">{error}</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="mt-4 px-4 py-2 rounded bg-gray-800 hover:bg-gray-700 text-gray-100 text-sm"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <p className="text-gray-500 text-sm">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
